import isApiError from "@insite/client-framework/Common/isApiError";
import {
    addToChainAfter,
    createHandlerChainRunner,
    Handler,
    HasOnError,
    HasOnSuccess,
} from "@insite/client-framework/HandlerCreator";
import { replaceInChain } from "@insite/client-framework/HandlerCreator";
import {
    GetAlsoPurchasedProductCollectionApiV2Parameter,
    getAlsoPurchasedProductsCollectionV2,
    GetProductCollectionApiV2Parameter,
    getProductCollectionV2,
    GetRelatedProductCollectionApiV2Parameter,
    getRelatedProductsCollectionV2,
} from "@insite/client-framework/Services/ProductServiceV2";
import { StorefrontAccess } from "@insite/client-framework/Services/SettingsService";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    chain,
    PopulateApiParameter,
    RequestDataFromApi,
} from "@insite/client-framework/Store/Data/Products/Handlers/LoadProducts";
import { getProductsDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { ProductCollectionModel, ProductModel } from "@insite/client-framework/Types/ApiModels";

type Parameter = (GetProductCollectionApiV2Parameter | GetRelatedProductCollectionApiV2Parameter) & {
    isLoadMore?: boolean;
} & HasOnSuccess<ProductModel[]> &
    HasOnError<string>;
type Props = {
    apiParameter: GetProductCollectionApiV2Parameter | GetRelatedProductCollectionApiV2Parameter;
    apiResult: ProductCollectionModel;
};

type HandlerType = Handler<Parameter, Props>;

export const PopulateApiParameterExtended: HandlerType = props => {
    const { onSuccess, onError, isLoadMore, ...parameter } = props.parameter;
    props.apiParameter = parameter;

    if (props.parameter.isLoadMore) {
        const prevApiParams: typeof props.apiParameter = { ...props.apiParameter };
        prevApiParams.page =
            props.apiParameter.page && props.apiParameter.page > 1 ? props.apiParameter.page - 1 : undefined;
        const prevProducts = getProductsDataView(props.getState(), prevApiParams).value;
        if (!prevProducts && props.parameter.page && props.parameter.page > 1) {
            props.apiParameter.page = 1;
            props.apiParameter.pageSize = props.parameter.pageSize! * props.parameter.page;
        }
    }
};

export const RequestDataFromApiExtended: HandlerType = async props => {
    const state = props.getState();
    const currentPage = getCurrentPage(state);
    const { storefrontAccess } = getSettingsCollection(state).productSettings;
    if (
        storefrontAccess === StorefrontAccess.SignInRequiredToBrowse &&
        !state.context.session.isAuthenticated &&
        (currentPage.fields.excludeFromSignInRequired || currentPage.type === "SignInPage")
    ) {
        return false;
    }

    try {
        if ("relationship" in props.apiParameter) {
            props.apiResult = await getRelatedProductsCollectionV2(props.apiParameter);
        } else {
            props.apiResult = await getProductCollectionV2(props.apiParameter);
        }
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            props.parameter.onError?.(error.errorJson.message);
            return false;
        }

        if (storefrontAccess === StorefrontAccess.SignInRequiredToBrowse && error.status === 403) {
            // auth timed out with SignInRequiredToBrowsee - throw this up as a 401 so DisplayError reloads the site
            error.status = 401;
            error.errorJson = { message: "error" };
        }

        throw error;
    }
};

replaceInChain(chain, PopulateApiParameter, PopulateApiParameterExtended);

replaceInChain(chain, RequestDataFromApi, RequestDataFromApiExtended);

addToChainAfter(chain, PopulateApiParameter, props => {
    props.apiParameter.expand?.push("properties");
});

const LoadProductsExtended = createHandlerChainRunner(chain, "LoadProductsExtended");
export default LoadProductsExtended;
